import { useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { ChevronRight, Database, AlertCircle } from 'lucide-react';
import { clsx } from 'clsx';
import { getProgramBySlug, type ProgramCode } from '@/config/programs';
import { apiConfig } from '@/config/apiConfig';
import { useAuth } from '@/hooks/useAuth';
import { useReviewableResources } from './hooks/useReviewableResources';
import {
  filterResourcesForProgram,
  hasProgramAccess,
  programCodeForResource,
} from './utils/programResource';
import type { ReviewableResource } from './types/resource';

export function ImpactDataPage() {
  const { programCode: slug } = useParams<{ programCode: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const program = getProgramBySlug(slug);
  const featureOn = apiConfig.features.enableImpactDataReview;

  // /review/resources accepts one program at a time; multi-code programs skip
  // the server filter and narrow via filterResourcesForProgram below.
  const programParam = program?.codes.length === 1 ? program.codes[0] : undefined;
  const { resources, loading, error } = useReviewableResources({ program: programParam });

  const programResources = useMemo(
    () => (program ? filterResourcesForProgram(resources, program) : []),
    [program, resources],
  );

  const codeGroups = useMemo(
    () => (program ? groupResourcesByCode(programResources, program.codes) : []),
    [programResources, program],
  );
  const [activeCode, setActiveCode] = useState<string | null>(null);
  const effectiveCode = activeCode ?? codeGroups[0]?.code ?? null;
  const visibleResources = useMemo(() => {
    if (!program || program.codes.length <= 1) return programResources;
    const group = codeGroups.find((g) => g.code === effectiveCode);
    return group ? group.resources : programResources;
  }, [program, programResources, codeGroups, effectiveCode]);

  if (!program) {
    return <NotFoundState />;
  }
  if (!hasProgramAccess(program, user?.roles)) {
    return <ForbiddenState label={program.label} />;
  }
  if (!featureOn) {
    return <DisabledState label={program.label} />;
  }

  if (loading) {
    return <LoadingState label={program.label} />;
  }
  if (error) {
    return <ErrorState label={program.label} message={error.message} />;
  }

  if (programResources.length === 0) {
    return <EmptyState label={program.label} totalResources={resources.length} />;
  }

  // Skip auto-nav for multi-code programs so the tab strip stays reachable.
  if (programResources.length === 1 && program.codes.length === 1) {
    return <Navigate to={`/programs/${program.slug}/impact-data/${programResources[0].title}`} replace />;
  }

  const showTabs = program.codes.length > 1 && codeGroups.length > 1;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <header>
        <p className="text-sm font-medium uppercase tracking-wider text-rgf-gray-500">
          {program.label}
        </p>
        <h1 className="mt-1 text-2xl font-semibold text-rgf-gray-900">Impact Data</h1>
        <p className="mt-2 text-sm text-rgf-gray-600">
          Review and update submissions collected via the Impact app. Pick a data type
          to open its review grid.
        </p>
      </header>

      {showTabs && (
        <div
          role="tablist"
          aria-label="Sub-brand"
          className="flex flex-wrap gap-1 border-b border-rgf-gray-200"
        >
          {codeGroups.map((group) => {
            const isActive = effectiveCode === group.code;
            return (
              <button
                key={group.code}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveCode(group.code)}
                className={clsx(
                  'inline-flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium transition',
                  isActive
                    ? 'border-rgf-primary-500 text-rgf-primary-700'
                    : 'border-transparent text-rgf-gray-500 hover:text-rgf-gray-900',
                )}
              >
                {group.label}
                <span
                  className={clsx(
                    'inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-xs font-semibold',
                    isActive
                      ? 'bg-rgf-primary-100 text-rgf-primary-700'
                      : 'bg-rgf-gray-100 text-rgf-gray-600',
                  )}
                >
                  {group.resources.length}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {visibleResources.map((resource) => (
          <button
            key={resource.title}
            type="button"
            onClick={() => navigate(`/programs/${program.slug}/impact-data/${resource.title}`)}
            className="group flex items-start gap-3 rounded-xl border border-rgf-gray-200 bg-white p-4 text-left shadow-sm transition hover:border-rgf-primary-400 hover:shadow"
          >
            <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-rgf-primary-50 text-rgf-primary-700">
              <Database className="h-5 w-5" />
            </span>
            <span className="flex flex-1 flex-col">
              <span className="text-sm font-medium text-rgf-gray-900">
                {resource.display_title || humanise(resource.title)}
              </span>
              <span className="mt-0.5 text-xs text-rgf-gray-500">{resource.title}</span>
            </span>
            <ChevronRight className="h-5 w-5 flex-shrink-0 text-rgf-gray-400 transition group-hover:translate-x-0.5 group-hover:text-rgf-primary-600" />
          </button>
        ))}
      </div>
    </div>
  );
}

interface CodeGroup {
  code: string;
  label: string;
  resources: ReviewableResource[];
}

// Un-attributable resources fall into the first code so nothing gets hidden.
function groupResourcesByCode(
  resources: ReviewableResource[],
  codes: ProgramCode[],
): CodeGroup[] {
  const buckets = new Map<string, ReviewableResource[]>();
  for (const code of codes) buckets.set(code, []);

  const known = new Set(codes.map((c) => c.toUpperCase()));
  for (const resource of resources) {
    const attributed = programCodeForResource(resource);
    const key = attributed && known.has(attributed) ? attributed : codes[0];
    buckets.get(key)!.push(resource);
  }

  return codes
    .map<CodeGroup>((code) => ({
      code,
      label: code,
      resources: buckets.get(code) ?? [],
    }))
    .filter((g) => g.resources.length > 0);
}

function humanise(name: string): string {
  return name
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── State views ─────────────────────────────────────────────────────

function NotFoundState() {
  return (
    <CenteredNotice
      title="Unknown program"
      body="The program you tried to open doesn't exist."
    />
  );
}

function ForbiddenState({ label }: { label: string }) {
  return (
    <CenteredNotice
      title="You don't have access"
      body={`You need a ${label} role to view its Impact Data.`}
    />
  );
}

function DisabledState({ label }: { label: string }) {
  return (
    <CenteredNotice
      title="Not enabled yet"
      body={`Impact Data review for ${label} isn't turned on in this environment.`}
    />
  );
}

function EmptyState({ label, totalResources }: { label: string; totalResources: number }) {
  // Show the raw total so we can tell the two zero cases apart:
  //   - server returned nothing at all (0/0 → backend not enabled yet)
  //   - server returned resources but none belong to this program
  //     (N/0 → other programs enabled, this one not yet)
  const body =
    totalResources === 0
      ? `There are no reviewable data types available for you right now. The maker-checker backend may not be enabled in this environment yet.`
      : `There are no ${label} data types available for you to review right now. (${totalResources} reviewable data type${totalResources === 1 ? '' : 's'} for other programs.)`;
  return <CenteredNotice title="No reviewable data yet" body={body} />;
}

function LoadingState({ label }: { label: string }) {
  return (
    <CenteredNotice
      title={`Loading ${label} data types…`}
      body="Fetching the list of reviewable resources."
      spinner
    />
  );
}

function ErrorState({ label, message }: { label: string; message: string }) {
  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
        <AlertCircle className="h-5 w-5 flex-shrink-0 text-red-600" />
        <div className="text-sm">
          <p className="font-medium text-red-900">Couldn't load {label} Impact Data</p>
          <p className="mt-1 text-red-700">{message}</p>
        </div>
      </div>
    </div>
  );
}

function CenteredNotice({
  title,
  body,
  spinner,
}: {
  title: string;
  body: string;
  spinner?: boolean;
}) {
  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="rounded-xl border border-rgf-gray-200 bg-white p-8 text-center">
        {spinner && (
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-rgf-primary-500 border-t-transparent" />
        )}
        <h2 className="text-lg font-semibold text-rgf-gray-900">{title}</h2>
        <p className="mt-2 text-sm text-rgf-gray-600">{body}</p>
      </div>
    </div>
  );
}
